import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router";
import PageMeta from "../../components/common/PageMeta";
import PageContainer from "../../components/common/PageContainer";
import PageHeader from "../../components/common/PageHeader";
import { filterPanelSx } from "../../components/common/filterStyles";
import { useApi } from "../../hooks/useApi";
import { useBrandId } from "../../hooks/useBrandId";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Box,
  Typography,
  Collapse,
  IconButton,
  Badge,
  Button,
  TextField,
  Menu,
  MenuItem,
  Checkbox,
  Divider,
  TableSortLabel,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import FilterListIcon from '@mui/icons-material/FilterList';
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs, { Dayjs } from "dayjs";
import {
  ClientVerifiFraudNotice,
  ClientVerifiFraudResponse,
  VerifiNoticeMerchantFilterItem,
  VerifiNoticeDescriptorFilterItem,
} from "../../types/verifiNotice";
import { formatVerifiStatusCode, formatVerifiReasonCode } from "../../types/alerts";
import { formatCurrency } from "../../utils/formatCurrency";
import * as Sentry from "@sentry/react";
import { pascalCase } from "../../utils/textUtils";
import { HasPermission } from "../../functions/auth";
import { PERMISSIONS } from "../../constants/permissions";
import ClientCsvDialog from "../../components/clientAlerts/ClientCsvDialog";
import SearchableMultiSelectFilter from "../../components/common/SearchableMultiSelectFilter";

// Column configuration
const COLUMNS_STORAGE_KEY = 'client_fraud_notices_visible_columns_v2';

interface ColumnConfig {
  id: string;
  label: string;
  isDefault: boolean;
  sortField?: string;
}

const allColumns: ColumnConfig[] = [
  { id: 'case_id', label: 'Case ID', isDefault: false },
  { id: 'case_event', label: 'Case Event', isDefault: true },
  { id: 'brand_name', label: 'Brand', isDefault: false },
  { id: 'merchant_name', label: 'Merchant', isDefault: true },
  { id: 'payment_descriptor', label: 'Descriptor', isDefault: true },
  { id: 'case_date', label: 'Case Date', isDefault: true, sortField: 'case_date' },
  { id: 'amount', label: 'Amount', isDefault: true },
  { id: 'card_number', label: 'Card Number', isDefault: true },
  { id: 'card_type', label: 'Card Type', isDefault: true },
  { id: 'reason_code', label: 'Reason', isDefault: true },
  { id: 'transaction_date', label: 'Transaction Date', isDefault: true },
  { id: 'outcome', label: 'Outcome', isDefault: false },
  { id: 'status_code', label: 'Status', isDefault: false },
  { id: 'auth_code', label: 'Auth Code', isDefault: false },
  { id: 'transaction_id', label: 'Transaction ID', isDefault: false },
  { id: 'acquirer_bin', label: 'Acquirer BIN', isDefault: false },
  { id: 'arn', label: 'ARN', isDefault: false },
  { id: 'card_acceptor_id', label: 'Card Acceptor ID', isDefault: false },
];

const defaultColumnIds = allColumns.filter(c => c.isDefault).map(c => c.id);
const validColumnIds = allColumns.map(c => c.id);

const getStoredColumns = (): string[] => {
  try {
    const stored = localStorage.getItem(COLUMNS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed.filter((id: string) => validColumnIds.includes(id));
      }
    }
  } catch (e) {
    console.error('Failed to parse stored columns', e);
  }
  return defaultColumnIds;
};

const saveStoredColumns = (columnIds: string[]) => {
  localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(columnIds));
};

const formatDateTime = (dateString: string | Date | null | undefined) => {
  if (!dateString) return '--';
  const dateStr = dateString instanceof Date ? dateString.toISOString() : dateString;
  return dayjs.utc(dateStr).format('MMM DD, YYYY HH:mm');
};

const formatDate = (dateString: string | null | undefined) => {
  if (!dateString) return '--';
  return dayjs.utc(dateString).format('MMM DD, YYYY');
};

const formatCardNumber = (cardBin?: string | null, cardLast4?: string | null) => {
  if (!cardBin && !cardLast4) return '--';
  if (cardBin && cardLast4) {
    return `${cardBin}****${cardLast4}`;
  }
  if (cardLast4) {
    return `****${cardLast4}`;
  }
  return cardBin || '--';
};

function FraudNoticeRow({
  notice, visibleColumns,
}: {
  notice: ClientVerifiFraudNotice;
  visibleColumns: string[];
}) {
  const [open, setOpen] = useState(false);

  const renderCell = (columnId: string) => {
    switch (columnId) {
      case 'case_event':
        return <TableCell key={columnId}>{notice.case_event ? pascalCase(notice.case_event) : '--'}</TableCell>;
      case 'payment_descriptor':
        return <TableCell key={columnId}>{notice.payment_descriptor || '--'}</TableCell>;
      case 'case_date':
        return <TableCell key={columnId}>{formatDateTime(notice.case_date)}</TableCell>;
      case 'amount':
        return (
          <TableCell key={columnId}>
            {notice.transaction_amount
              ? formatCurrency(notice.transaction_amount, notice.transaction_currency || 'USD', true)
              : '--'}
          </TableCell>
        );
      case 'card_number':
        return <TableCell key={columnId}>{formatCardNumber(notice.card_bin, notice.card_last4)}</TableCell>;
      case 'card_type':
        return <TableCell key={columnId}>{notice.payment_type ? (notice.payment_type) : '--'}</TableCell>;
      case 'outcome':
        return <TableCell key={columnId}>{notice.outcome ? pascalCase(notice.outcome) : '--'}</TableCell>;
      case 'auth_code':
        return <TableCell key={columnId}>{notice.auth_code || '--'}</TableCell>;
      case 'transaction_id':
        return <TableCell key={columnId}>{notice.transaction_id || '--'}</TableCell>;
      case 'brand_name':
        return <TableCell key={columnId}>{notice.brand_name || '--'}</TableCell>;
      case 'merchant_name':
        return <TableCell key={columnId}>{notice.merchant_name || '--'}</TableCell>;
      case 'case_id':
        return <TableCell key={columnId}>{notice.case_id}</TableCell>;
      case 'acquirer_bin':
        return <TableCell key={columnId}>{notice.acquirer_bin || '--'}</TableCell>;
      case 'arn':
        return <TableCell key={columnId}>{notice.arn || '--'}</TableCell>;
      case 'card_acceptor_id':
        return <TableCell key={columnId}>{notice.card_acceptor_id || '--'}</TableCell>;
      case 'reason_code':
        return <TableCell key={columnId}>{formatVerifiReasonCode(notice.reason_code)}</TableCell>;
      case 'status_code':
        return <TableCell key={columnId}>{formatVerifiStatusCode(notice.status_code)}</TableCell>;
      case 'transaction_date':
        return <TableCell key={columnId}>{formatDate(notice.transaction_date)}</TableCell>;
      default:
        return null;
    }
  };

  return (
    <React.Fragment>
      <TableRow key={notice.verifi_notice_id} hover sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell sx={{ width: 48, padding: '4px !important', textAlign: 'center', verticalAlign: 'middle' }}>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
            sx={{ padding: '4px', margin: '0 auto', display: 'flex' }}
          >
            {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        {allColumns.filter(c => visibleColumns.includes(c.id)).map(c => renderCell(c.id))}
        <TableCell />
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={visibleColumns.length + 2}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
                Notice Details
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Case ID</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>{notice.case_id}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Case Event</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>{notice.case_event ? pascalCase(notice.case_event) : '--'}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Outcome</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>{notice.outcome ? pascalCase(notice.outcome) : '--'}</Typography>
                </Box>
                {notice.reason_code && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">Reason</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{formatVerifiReasonCode(notice.reason_code)}</Typography>
                  </Box>
                )}
                {notice.status_code && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">Status</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{formatVerifiStatusCode(notice.status_code)}</Typography>
                  </Box>
                )}
                {notice.merchant_name && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">Merchant</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{notice.merchant_name}</Typography>
                  </Box>
                )}
                {notice.payment_descriptor && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">Descriptor</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{notice.payment_descriptor}</Typography>
                  </Box>
                )}
                {notice.acquirer_bin && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">Acquirer BIN</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{notice.acquirer_bin}</Typography>
                  </Box>
                )}
                {notice.card_acceptor_id && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">Card Acceptor ID</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{notice.card_acceptor_id}</Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="body2" color="text.secondary">Amount</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {notice.transaction_amount
                      ? formatCurrency(notice.transaction_amount, notice.transaction_currency || 'USD', true)
                      : '--'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Card Number</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>{formatCardNumber(notice.card_bin, notice.card_last4)}</Typography>
                </Box>
                {notice.payment_type && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">Card Type</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{notice.payment_type}</Typography>
                  </Box>
                )}
                {notice.auth_code && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">Auth Code</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{notice.auth_code}</Typography>
                  </Box>
                )}
                {notice.arn && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">ARN</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{notice.arn}</Typography>
                  </Box>
                )}
                {notice.transaction_id && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">Transaction ID</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{notice.transaction_id}</Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="body2" color="text.secondary">Transaction Date</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>{formatDate(notice.transaction_date)}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Case Date</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>{formatDateTime(notice.case_date)}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Created At</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>{formatDateTime(notice.created_at)}</Typography>
                </Box>
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

export default function ClientFraudNotices() {
  const { brandId } = useBrandId();
  const { get, loading, error: apiError } = useApi();
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();

  // Read initial values from URL or use defaults
  const initialStartDate = urlSearchParams.get("startDate") ? dayjs.utc(urlSearchParams.get("startDate")) : dayjs.utc().subtract(7, 'day');
  const initialEndDate = urlSearchParams.get("endDate") ? dayjs.utc(urlSearchParams.get("endDate")) : dayjs.utc();
  const initialCaseId = urlSearchParams.get("caseId") || '';
  const initialPage = urlSearchParams.get("page") ? Math.max(0, parseInt(urlSearchParams.get("page")!, 10)) : 0;
  const initialRowsPerPage = urlSearchParams.get("rowsPerPage") ? parseInt(urlSearchParams.get("rowsPerPage")!, 10) : 25;
  const initialSortBy = urlSearchParams.get("sortBy") || 'case_date';
  const initialSortOrder = (urlSearchParams.get("sortOrder") === 'asc' ? 'asc' : 'desc') as 'asc' | 'desc';
  const initialMerchantIds = (urlSearchParams.get("merchantIds") || '').split(',').filter(Boolean);
  const initialDescriptorIds = (urlSearchParams.get("descriptorIds") || '').split(',').filter(Boolean);

  const [startDate, setStartDate] = useState<Dayjs | null>(initialStartDate);
  const [endDate, setEndDate] = useState<Dayjs | null>(initialEndDate);
  const [caseId, setCaseId] = useState<string>(initialCaseId);
  const [merchantIds, setMerchantIds] = useState<string[]>(initialMerchantIds);
  const [descriptorIds, setDescriptorIds] = useState<string[]>(initialDescriptorIds);
  const hasActiveFilters = merchantIds.length > 0 || descriptorIds.length > 0;
  const [filtersExpanded, setFiltersExpanded] = useState(hasActiveFilters);
  const activeFiltersCount = (merchantIds.length > 0 ? 1 : 0) + (descriptorIds.length > 0 ? 1 : 0);
  const [noticeMerchants, setNoticeMerchants] = useState<VerifiNoticeMerchantFilterItem[]>([]);
  const [noticeDescriptors, setNoticeDescriptors] = useState<VerifiNoticeDescriptorFilterItem[]>([]);
  const [noticesData, setNoticesData] = useState<ClientVerifiFraudResponse | null>(null);
  const [page, setPage] = useState(initialPage);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
  const [sortBy, setSortBy] = useState<string>(initialSortBy);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(initialSortOrder);
  const [pageError, setPageError] = useState<string | null>(null);

  // CSV export
  const [csvDialogOpen, setCsvDialogOpen] = useState(false);

  // Column configuration
  const [visibleColumns, setVisibleColumns] = useState<string[]>(getStoredColumns);
  const [columnMenuAnchor, setColumnMenuAnchor] = useState<null | HTMLElement>(null);
  const columnMenuOpen = Boolean(columnMenuAnchor);
  const [startPickerOpen, setStartPickerOpen] = useState(false);
  const [endPickerOpen, setEndPickerOpen] = useState(false);

  const merchantOptions = noticeMerchants.map((merchant) => ({ value: String(merchant.merchant_id), label: merchant.merchant_name }));
  const descriptorOptions = noticeDescriptors.map((desc) => ({ value: String(desc.descriptor_id), label: desc.descriptor_name }));

  const handleColumnMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setColumnMenuAnchor(event.currentTarget);
  };

  const handleColumnMenuClose = () => {
    setColumnMenuAnchor(null);
  };

  const handleColumnToggle = (columnId: string) => {
    setVisibleColumns(prev => {
      const newColumns = prev.includes(columnId)
        ? prev.filter(id => id !== columnId)
        : [...prev, columnId];
      saveStoredColumns(newColumns);
      return newColumns;
    });
  };

  const handleResetColumns = () => {
    setVisibleColumns(defaultColumnIds);
    saveStoredColumns(defaultColumnIds);
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setPage(0);
  };

  // Update URL whenever search parameters change
  useEffect(() => {
    const params = new URLSearchParams(urlSearchParams);

    if (startDate) {
      params.set("startDate", startDate.format("YYYY-MM-DD"));
    } else {
      params.delete("startDate");
    }

    if (endDate) {
      params.set("endDate", endDate.format("YYYY-MM-DD"));
    } else {
      params.delete("endDate");
    }

    if (caseId) {
      params.set("caseId", caseId);
    } else {
      params.delete("caseId");
    }

    if (page > 0) {
      params.set("page", page.toString());
    } else {
      params.delete("page");
    }

    if (rowsPerPage > 0) {
      params.set("rowsPerPage", rowsPerPage.toString());
    } else {
      params.delete("rowsPerPage");
    }

    if (sortBy && sortBy !== 'case_date') {
      params.set("sortBy", sortBy);
    } else {
      params.delete("sortBy");
    }

    if (sortOrder !== 'desc') {
      params.set("sortOrder", sortOrder);
    } else {
      params.delete("sortOrder");
    }

    if (merchantIds.length > 0) {
      params.set("merchantIds", merchantIds.join(','));
    } else {
      params.delete("merchantIds");
    }

    if (descriptorIds.length > 0) {
      params.set("descriptorIds", descriptorIds.join(','));
    } else {
      params.delete("descriptorIds");
    }

    setUrlSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, caseId, merchantIds, descriptorIds, page, rowsPerPage, sortBy, sortOrder]);

  const fetchNoticeMerchants = useCallback(async () => {
    try {
      const response = await get<{ merchants: VerifiNoticeMerchantFilterItem[] }>(`/api/client-alerts/verifi-notices/merchants`);
      setNoticeMerchants(response.merchants);
    } catch (err) {
      console.error('Failed to fetch fraud merchants:', err);
      Sentry.captureException(err);
    }
  }, [get]);

  const fetchNoticeDescriptors = useCallback(async () => {
    try {
      const response = await get<{ descriptors: VerifiNoticeDescriptorFilterItem[] }>(`/api/client-alerts/verifi-notices/descriptors`);
      setNoticeDescriptors(response.descriptors);
    } catch (err) {
      console.error('Failed to fetch fraud descriptors:', err);
      Sentry.captureException(err);
    }
  }, [get]);

  useEffect(() => {
    fetchNoticeMerchants();
  }, [fetchNoticeMerchants]);

  useEffect(() => {
    fetchNoticeDescriptors();
  }, [fetchNoticeDescriptors]);

  const fetchNotices = useCallback(async () => {
    if (!startDate || !endDate) {
      return;
    }

    setPageError(null);

    try {
      const params = new URLSearchParams({
        startDate: startDate.format('YYYY-MM-DD'),
        endDate: endDate.format('YYYY-MM-DD'),
        ...(caseId && { caseId }),
        ...(merchantIds.length > 0 && { merchantIds: merchantIds.join(',') }),
        ...(descriptorIds.length > 0 && { descriptorIds: descriptorIds.join(',') }),
        ...(brandId && { brandId: String(brandId) }),
        page: (page + 1).toString(),
        limit: rowsPerPage.toString(),
        sortBy,
        sortOrder,
      });

      const response = await get<ClientVerifiFraudResponse>(`/api/client-alerts/verifi-fraud?${params.toString()}`);
      setNoticesData(response);
    } catch (err) {
      console.error('Failed to fetch fraud notices:', err);
      setPageError(err instanceof Error ? err.message : 'Unknown error');
      Sentry.captureException(err);
      setNoticesData(null);
    }
  }, [brandId, get, startDate, endDate, caseId, merchantIds, descriptorIds, page, rowsPerPage, sortBy, sortOrder]);

  useEffect(() => {
    fetchNotices();
  }, [fetchNotices]);

  const handleStartDateChange = (date: Dayjs | null) => {
    setStartDate(date);
    setPage(0);
  };

  const handleEndDateChange = (date: Dayjs | null) => {
    setEndDate(date);
    setPage(0);
  };

  const handleChangePage = async (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = async (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <div>
      <PageMeta
        title="Chargemont | Fraud Notices (TC40)"
        description="Chargemont | Fraud Notices (TC40)"
      />
      <PageContainer>
        <PageHeader
          title="Fraud Notices (TC40)"
          action={
            <HasPermission permission={PERMISSIONS.ALERTS_OWN}>
              <Button
                variant="contained"
                onClick={() => setCsvDialogOpen(true)}
                sx={{
                  backgroundColor: '#465fff',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#3641f5',
                  },
                }}
              >
                Export CSV
              </Button>
            </HasPermission>
          }
        />

        {/* Filters */}
        <Box
          className="mb-6"
          sx={filterPanelSx}
        >
          {/* First Row: Date Pickers + Filter Toggle */}
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2} sx={{ p: 2 }}>
            <Box display="flex" alignItems="center" flexWrap="wrap" gap={2}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box display="flex" alignItems="center" gap={1}>
                  <DatePicker
                    label="Start"
                    value={startDate}
                    onChange={handleStartDateChange}
                    open={startPickerOpen}
                    onOpen={() => setStartPickerOpen(true)}
                    onClose={() => setStartPickerOpen(false)}
                    slotProps={{
                      textField: {
                        size: "small",
                        sx: {
                          width: "fit-content",
                        },
                        onClick: () => setStartPickerOpen(true),
                      },
                    }}
                  />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    —
                  </Typography>
                  <DatePicker
                    label="End"
                    value={endDate}
                    onChange={handleEndDateChange}
                    open={endPickerOpen}
                    onOpen={() => setEndPickerOpen(true)}
                    onClose={() => setEndPickerOpen(false)}
                    slotProps={{
                      textField: {
                        size: "small",
                        sx: {
                          width: "fit-content",
                        },
                        onClick: () => setEndPickerOpen(true),
                      },
                    }}
                  />
                </Box>
              </LocalizationProvider>
            </Box>

            {/* Right side: Case ID search + Filter Toggle */}
            <Box display="flex" alignItems="center" flexWrap="wrap" gap={2}>
              <TextField
                size="small"
                label="Case ID"
                value={caseId}
                onChange={(e) => {
                  setCaseId(e.target.value);
                  setPage(0);
                }}
                sx={{ minWidth: 160 }}
                placeholder="Search..."
              />
              <IconButton
                onClick={() => setFiltersExpanded(!filtersExpanded)}
                size="small"
                sx={{
                  border: '1px solid',
                  borderColor: filtersExpanded ? 'primary.main' : 'divider',
                  borderRadius: 1,
                  backgroundColor: filtersExpanded ? 'primary.50' : 'transparent',
                  '&:hover': {
                    backgroundColor: filtersExpanded ? 'primary.100' : 'action.hover',
                  },
                }}
              >
                <Badge
                  badgeContent={activeFiltersCount}
                  color="primary"
                  sx={{
                    '& .MuiBadge-badge': {
                      fontSize: 10,
                      height: 16,
                      minWidth: 16,
                    },
                  }}
                >
                  <FilterListIcon
                    fontSize="small"
                    sx={{ color: filtersExpanded ? 'primary.main' : 'text.secondary' }}
                  />
                </Badge>
              </IconButton>
            </Box>
          </Box>

          {/* Second Row: Merchant + Descriptor (collapsible) */}
          <Collapse in={filtersExpanded}>
            <Box
              display="flex"
              alignItems="center"
              flexWrap="wrap"
              gap={2}
              sx={{
                px: 2,
                pb: 2,
                pt: 1.5,
                borderTop: '1px solid',
                borderColor: 'divider',
              }}
            >
              <SearchableMultiSelectFilter
                label="Merchant"
                options={merchantOptions}
                value={merchantIds}
                width={200}
                searchPlaceholder="Search merchants…"
                showSelectAllClearAll
                onChange={(values) => { setMerchantIds(values); setPage(0); }}
              />
              <SearchableMultiSelectFilter
                label="Descriptor"
                options={descriptorOptions}
                value={descriptorIds}
                width={200}
                searchPlaceholder="Search descriptors…"
                showSelectAllClearAll
                onChange={(values) => { setDescriptorIds(values); setPage(0); }}
              />
            </Box>
          </Collapse>
        </Box>

        {/* Error State */}
        {(apiError || pageError) && (
          <Box className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            Error: {apiError || pageError}
          </Box>
        )}

        {/* Data Table */}
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell></TableCell>
                  {allColumns.filter(c => visibleColumns.includes(c.id)).map(column => (
                    <TableCell key={column.id} sx={{ whiteSpace: 'nowrap' }}>
                      {column.sortField ? (
                        <TableSortLabel
                          active={sortBy === column.sortField}
                          direction={sortBy === column.sortField ? sortOrder : 'asc'}
                          onClick={() => handleSort(column.sortField!)}
                        >
                          {column.label}
                        </TableSortLabel>
                      ) : (
                        column.label
                      )}
                    </TableCell>
                  ))}
                  <TableCell sx={{ textAlign: 'center' }}>
                    <IconButton
                      onClick={handleColumnMenuOpen}
                      size="small"
                      sx={{
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      }}
                    >
                      <SettingsOutlinedIcon fontSize="medium" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={visibleColumns.length + 2} align="center">
                      <Typography variant="body2" color="text.secondary">
                        Loading...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : noticesData?.notices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={visibleColumns.length + 2} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No fraud notices found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  noticesData?.notices.map((notice) => (
                    <FraudNoticeRow key={notice.verifi_notice_id} notice={notice} visibleColumns={visibleColumns} />
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {noticesData && (
            <TablePagination
              rowsPerPageOptions={[25, 50, 100]}
              component="div"
              count={noticesData.pagination.totalItems}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </Paper>
      </PageContainer>

      {/* Column Settings Menu */}
      <Menu
        anchorEl={columnMenuAnchor}
        open={columnMenuOpen}
        onClose={handleColumnMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: {
              maxHeight: 400,
              width: 250,
            },
          },
        }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Configure Columns
          </Typography>
        </Box>
        <Divider />
        <Box sx={{ px: 1 }}>
          {allColumns.map((column) => (
            <MenuItem
              key={column.id}
              dense
              sx={{ py: 0.5 }}
              onClick={() => handleColumnToggle(column.id)}
            >
              <Checkbox
                checked={visibleColumns.includes(column.id)}
                size="small"
                sx={{ py: 0, mr: 1 }}
              />
              <Typography variant="body2">{column.label}</Typography>
            </MenuItem>
          ))}
        </Box>
        <Divider />
        <MenuItem onClick={handleResetColumns} dense>
          <Typography variant="body2" color="primary">
            Reset to Default
          </Typography>
        </MenuItem>
      </Menu>
      <ClientCsvDialog open={csvDialogOpen} onClose={() => setCsvDialogOpen(false)} title="Export Fraud Notices CSV" downloadUrl="/api/client-alerts/verifi-fraud/csv" />
    </div>
  );
}
